import { useEffect, useState } from "react"
import { Checkbox } from "@/components/ui/checkbox"
import { SaveChangesButton } from "@/styles/StandardButtons"
import { useAdminGuard } from "../AdminOnlyGuard"
import { useCompanyWorkingDays } from "@/features/project-management/schedule/useCompanyWorkingDays"
import { DEFAULT_WORKING_DAYS, type WorkingDaysConfig } from "@/features/project-management/schedule/schedule-date-engine"

const DAY_ORDER: { key: keyof WorkingDaysConfig; label: string }[] = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
]

/**
 * Settings -> Company -> Working Days. The company's working-day
 * configuration is the single source of truth the whole Schedule feature
 * (Gantt, List, Calendar) reads through `useCompanyWorkingDays` /
 * `schedule-date-engine.ts` for working-day duration math -- this section
 * is the ONLY place it's edited. Lives directly in Company Settings
 * (no separate Settings -> Schedule area), reusing the same
 * `company_settings` key/value store `share_link_expiration_days` already
 * uses (config-service.js), not a Gantt-specific table.
 */
export function WorkingDaysSection({ companyId, userId }: { companyId?: string | null; userId?: string }) {
  const canApply = useAdminGuard()
  const { workingDays, loading, saveWorkingDays } = useCompanyWorkingDays(companyId)
  const [draft, setDraft] = useState<WorkingDaysConfig>(DEFAULT_WORKING_DAYS)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (!loading) setDraft(workingDays)
  }, [loading, workingDays])

  const dirty = DAY_ORDER.some((d) => draft[d.key] !== workingDays[d.key])

  const toggleDay = (key: keyof WorkingDaysConfig) => {
    setSaved(false)
    setDraft((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const handleSave = async () => {
    if (!canApply()) return
    setSaving(true)
    const ok = await saveWorkingDays(draft, userId)
    setSaving(false)
    setSaved(ok)
  }

  if (loading) return null

  return (
    <div className="mt-8 border-t border-gray-200 pt-6">
      <h3 className="font-medium mb-1">Working Days</h3>
      <p className="text-sm text-gray-500 mb-4">
        Which days count as working days for Schedule duration calculations (Gantt, List, Calendar). Moving or resizing a task
        always preserves its working-day duration against this configuration.
      </p>

      <div className="flex flex-wrap gap-4 mb-4">
        {DAY_ORDER.map((d) => (
          <label key={d.key} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <Checkbox checked={draft[d.key]} onCheckedChange={() => toggleDay(d.key)} />
            {d.label}
          </label>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <SaveChangesButton onClick={handleSave} disabled={!dirty || saving} type="button">
          {saving ? "Saving…" : "Save Working Days"}
        </SaveChangesButton>
        {saved && !dirty && <span className="text-sm text-gray-500">Saved</span>}
      </div>
    </div>
  )
}
